import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export class StartMenu {
    constructor() {
        this.visible = false;
        this.container = null;
        this.gameStarted = false;
        this.onStartGame = null;
        this.onResumeGame = null;
        this.onRestartGame = null; // Add restart game callback
        this.onSettingChanged = null; // Add setting changed callback
        this.blurOverlay = null;
        this.menuCamera = null;
        this.menuControls = null;
        this.gameState = 'menu'; // Add game state tracking
        this.startButton = null; // Reference to start button for text updates
        this.resumeButton = null;
        this.controlsPanel = null;
        this.settingsPanel = null; // Add settings panel reference
    }

    init(scene, renderer) {
        this.createMenuUI();
        this.createBlurOverlay();
        this.setupMenuCamera(scene, renderer);
        // Show menu by default when game first loads
        this.toggleVisibility(true);
        // Initialize with menu state
        this.updateGameState('menu');
    }

    setupMenuCamera(scene, renderer) {
        // Create a separate camera for the menu view
        const aspect = window.innerWidth / window.innerHeight;
        this.menuCamera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
        this.menuCamera.position.set(50, 70, 50); // High angle diagonal view
        this.menuCamera.lookAt(0, 0, 0);
        
        // Set up controls for subtle movement in menu background
        this.menuControls = new OrbitControls(this.menuCamera, renderer.domElement);
        this.menuControls.enableDamping = true;
        this.menuControls.dampingFactor = 0.05;
        this.menuControls.enableZoom = false;
        this.menuControls.autoRotate = true;
        this.menuControls.autoRotateSpeed = 0.5;
        this.menuControls.minPolarAngle = Math.PI / 4; // Restrict vertical rotation
        this.menuControls.maxPolarAngle = Math.PI / 3;
    }

    updateMenuView(deltaTime) {
        if (this.visible && this.menuControls) {
            this.menuControls.update();
        }
    }

    createBlurOverlay() {
        const blurOverlay = document.createElement('div');
        blurOverlay.id = 'blur-overlay';
        blurOverlay.style.position = 'fixed';
        blurOverlay.style.top = '0';
        blurOverlay.style.left = '0';
        blurOverlay.style.width = '100%';
        blurOverlay.style.height = '100%';
        blurOverlay.style.backdropFilter = 'blur(5px)';
        blurOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
        blurOverlay.style.zIndex = '1900';
        blurOverlay.style.display = this.visible ? 'block' : 'none';
        
        document.body.appendChild(blurOverlay);
        this.blurOverlay = blurOverlay;
    }

    createMenuUI() {
        // Remove existing menu if any
        const existingMenu = document.getElementById('start-menu-container');
        if (existingMenu) {
            existingMenu.remove();
        }

        // Create container
        const container = document.createElement('div');
        container.id = 'start-menu-container';
        container.style.position = 'fixed';
        container.style.top = '50%';
        container.style.left = '50%';
        container.style.transform = 'translate(-50%, -50%)';
        container.style.backgroundColor = 'rgba(49, 40, 31, 0.9)'; // Creamy dark color
        container.style.color = '#f5f5dc'; // Beige text
        container.style.padding = '30px';
        container.style.borderRadius = '10px';
        container.style.fontFamily = 'Arial, sans-serif';
        container.style.fontSize = '18px';
        container.style.zIndex = '2001'; // Above blur overlay
        container.style.minWidth = '400px';
        container.style.maxWidth = '600px';
        container.style.display = this.visible ? 'block' : 'none';
        container.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.5)'; // Gold glow
        container.style.border = '2px solid rgba(255, 215, 0, 0.7)'; // Gold border
        
        // Game name placeholder
        const gameNamePlaceholder = document.createElement('div');
        gameNamePlaceholder.style.width = '350px';
        gameNamePlaceholder.style.height = '100px';
        gameNamePlaceholder.style.backgroundColor = '#3366cc'; // Blue placeholder
        gameNamePlaceholder.style.margin = '0 auto 30px';
        gameNamePlaceholder.style.borderRadius = '5px';
        gameNamePlaceholder.style.display = 'flex';
        gameNamePlaceholder.style.justifyContent = 'center';
        gameNamePlaceholder.style.alignItems = 'center';
        gameNamePlaceholder.style.color = 'white';
        gameNamePlaceholder.style.fontWeight = 'bold';
        gameNamePlaceholder.style.fontSize = '32px';
        gameNamePlaceholder.textContent = 'GET IN SHAPE';
        
        // Create menu buttons
        const buttons = [];
        
        // Start Game button
        const startGameButton = this.createButton('Start Game');
        this.startButton = startGameButton; // Store reference to update text later
        startGameButton.addEventListener('click', () => {
            this.gameStarted = true;
            this.toggleVisibility(false);
            
            // If in paused state, call restart game function
            if (this.gameState === 'paused') {
                if (this.onRestartGame) {
                    this.onRestartGame();
                }
            } else {
                // Otherwise call regular start game function
                if (this.onStartGame) {
                    this.onStartGame();
                }
            }
        });
        buttons.push(startGameButton);
        
        // Resume Game button (only shown after game has started)
        const resumeGameButton = this.createButton('Resume Game');
        resumeGameButton.style.display = 'none'; // Hide initially
        resumeGameButton.addEventListener('click', () => {
            this.toggleVisibility(false);
            if (this.onResumeGame) {
                this.onResumeGame();
            }
        });
        this.resumeButton = resumeGameButton;
        buttons.push(resumeGameButton);
        
        // Controls button
        const controlsButton = this.createButton('Controls');
        controlsButton.addEventListener('click', () => {
            this.toggleControlsPanel();
        });
        buttons.push(controlsButton);
        
        // Settings button
        const settingsButton = this.createButton('Settings');
        settingsButton.addEventListener('click', () => {
            this.toggleSettingsPanel();
        });
        buttons.push(settingsButton);
        
        // Create controls panel (hidden by default)
        const controlsPanel = this.createControlsPanel();
        controlsPanel.style.display = 'none';
        this.controlsPanel = controlsPanel;
        
        // Create settings panel (hidden by default)
        const settingsPanel = this.createSettingsPanel();
        settingsPanel.style.display = 'none';
        this.settingsPanel = settingsPanel;
        
        // Assemble UI
        container.appendChild(gameNamePlaceholder);
        buttons.forEach(button => container.appendChild(button));
        container.appendChild(controlsPanel);
        container.appendChild(settingsPanel);
        
        document.body.appendChild(container);
        this.container = container;
    }

    createButton(text) {
        const button = document.createElement('button');
        button.textContent = text;
        button.style.display = 'block';
        button.style.width = '100%';
        button.style.margin = '15px 0';
        button.style.padding = '12px 20px';
        button.style.backgroundColor = 'rgba(69, 60, 51, 0.9)'; // Darker than container
        button.style.color = '#f5f5dc'; // Beige text
        button.style.border = '1px solid rgba(255, 215, 0, 0.5)'; // Gold border
        button.style.borderRadius = '5px';
        button.style.cursor = 'pointer';
        button.style.fontSize = '18px';
        button.style.transition = 'all 0.2s ease';
        
        button.addEventListener('mouseover', () => {
            button.style.backgroundColor = 'rgba(89, 80, 71, 0.9)'; // Lighter on hover
            button.style.borderColor = 'rgba(255, 215, 0, 0.8)'; // More gold on hover
        });
        
        button.addEventListener('mouseout', () => {
            button.style.backgroundColor = 'rgba(69, 60, 51, 0.9)';
            button.style.borderColor = 'rgba(255, 215, 0, 0.5)';
        });
        
        return button;
    }

    createControlsPanel() {
        const panel = document.createElement('div');
        panel.style.backgroundColor = 'rgba(39, 30, 21, 0.95)';
        panel.style.padding = '20px';
        panel.style.borderRadius = '5px';
        panel.style.marginTop = '20px';
        panel.style.border = '1px solid rgba(255, 215, 0, 0.3)';
        
        const title = document.createElement('h3');
        title.textContent = 'Game Controls';
        title.style.marginBottom = '15px';
        title.style.textAlign = 'center';
        title.style.color = 'rgba(255, 215, 0, 0.8)';
        
        const controlsList = document.createElement('ul');
        controlsList.style.listStyleType = 'none';
        controlsList.style.padding = '0';
        
        const controls = [
            { key: 'W/S', action: 'Move forward/backward' },
            { key: 'A/D', action: 'Rotate left/right' },
            { key: 'SPACE', action: 'Jump (hold to charge)' },
            { key: 'Mouse', action: 'Control camera (click & drag)' },
            { key: 'T', action: 'Toggle camera mode' },
            { key: 'P', action: 'Toggle debug statistics' },
            { key: 'B', action: 'Toggle collision visualization' },
            { key: 'ESC', action: 'Open/close menu' }
        ];
        
        controls.forEach(control => {
            const item = document.createElement('li');
            item.style.margin = '10px 0';
            item.style.display = 'flex';
            
            const keySpan = document.createElement('span');
            keySpan.textContent = control.key;
            keySpan.style.fontWeight = 'bold';
            keySpan.style.minWidth = '80px';
            keySpan.style.display = 'inline-block';
            keySpan.style.color = 'rgba(255, 215, 0, 0.8)';
            
            const actionSpan = document.createElement('span');
            actionSpan.textContent = control.action;
            
            item.appendChild(keySpan);
            item.appendChild(actionSpan);
            controlsList.appendChild(item);
        });
        
        // Jump charging explanation
        const jumpTitle = document.createElement('h4');
        jumpTitle.textContent = 'Jump Charging System';
        jumpTitle.style.marginTop = '20px';
        jumpTitle.style.marginBottom = '10px';
        jumpTitle.style.color = 'rgba(255, 215, 0, 0.8)';
        
        const jumpExplanation = document.createElement('p');
        jumpExplanation.textContent = 'Hold SPACE to charge your jump. Release when the charge bar is in the green zone for maximum height, orange for medium height, or blue for a lower jump.';
        jumpExplanation.style.marginBottom = '15px';
        jumpExplanation.style.lineHeight = '1.4';
        
        // Back button
        const backButton = document.createElement('button');
        backButton.textContent = 'Back to Menu';
        backButton.style.display = 'block';
        backButton.style.width = '100%';
        backButton.style.margin = '20px 0 5px';
        backButton.style.padding = '10px';
        backButton.style.backgroundColor = 'rgba(69, 60, 51, 0.9)';
        backButton.style.color = '#f5f5dc';
        backButton.style.border = '1px solid rgba(255, 215, 0, 0.5)';
        backButton.style.borderRadius = '5px';
        backButton.style.cursor = 'pointer';
        
        backButton.addEventListener('click', () => {
            this.toggleControlsPanel();
        });
        
        backButton.addEventListener('mouseover', () => {
            backButton.style.backgroundColor = 'rgba(89, 80, 71, 0.9)';
            backButton.style.borderColor = 'rgba(255, 215, 0, 0.8)';
        });
        
        backButton.addEventListener('mouseout', () => {
            backButton.style.backgroundColor = 'rgba(69, 60, 51, 0.9)';
            backButton.style.borderColor = 'rgba(255, 215, 0, 0.5)';
        });
        
        panel.appendChild(title);
        panel.appendChild(controlsList);
        panel.appendChild(jumpTitle);
        panel.appendChild(jumpExplanation);
        panel.appendChild(backButton);
        
        return panel;
    }

    createSettingsPanel() {
        const panel = document.createElement('div');
        panel.style.backgroundColor = 'rgba(39, 30, 21, 0.95)';
        panel.style.padding = '20px';
        panel.style.borderRadius = '5px';
        panel.style.marginTop = '20px';
        panel.style.border = '1px solid rgba(255, 215, 0, 0.3)';
        
        const title = document.createElement('h3');
        title.textContent = 'Game Settings';
        title.style.marginBottom = '15px';
        title.style.textAlign = 'center';
        title.style.color = 'rgba(255, 215, 0, 0.8)';
        panel.appendChild(title);
        
        // Camera Mode Setting
        const cameraSection = document.createElement('div');
        cameraSection.style.marginBottom = '20px';
        
        const cameraTitle = document.createElement('h4');
        cameraTitle.textContent = 'Camera Settings';
        cameraTitle.style.color = 'rgba(255, 215, 0, 0.7)';
        cameraTitle.style.marginBottom = '10px';
        cameraSection.appendChild(cameraTitle);
        
        // Camera Mode Toggle
        const cameraToggleContainer = document.createElement('div');
        cameraToggleContainer.style.display = 'flex';
        cameraToggleContainer.style.alignItems = 'center';
        cameraToggleContainer.style.marginBottom = '10px';
        
        const cameraToggleLabel = document.createElement('label');
        cameraToggleLabel.textContent = 'Follow Player Direction';
        cameraToggleLabel.style.marginRight = '10px';
        cameraToggleLabel.style.flexGrow = '1';
        
        const cameraToggle = document.createElement('input');
        cameraToggle.type = 'checkbox';
        cameraToggle.checked = true; // Default to true
        cameraToggle.style.transform = 'scale(1.2)';
        cameraToggle.id = 'camera-follow-toggle';
        
        cameraToggleContainer.appendChild(cameraToggleLabel);
        cameraToggleContainer.appendChild(cameraToggle);
        cameraSection.appendChild(cameraToggleContainer);
        
        // Display Stats Setting
        const displaySection = document.createElement('div');
        displaySection.style.marginBottom = '20px';
        
        const displayTitle = document.createElement('h4');
        displayTitle.textContent = 'Display Settings';
        displayTitle.style.color = 'rgba(255, 215, 0, 0.7)';
        displayTitle.style.marginBottom = '10px';
        displaySection.appendChild(displayTitle);
        
        // FPS Counter Toggle
        const fpsToggleContainer = document.createElement('div');
        fpsToggleContainer.style.display = 'flex';
        fpsToggleContainer.style.alignItems = 'center';
        fpsToggleContainer.style.marginBottom = '10px';
        
        const fpsToggleLabel = document.createElement('label');
        fpsToggleLabel.textContent = 'Show FPS Counter';
        fpsToggleLabel.style.marginRight = '10px';
        fpsToggleLabel.style.flexGrow = '1';
        
        const fpsToggle = document.createElement('input');
        fpsToggle.type = 'checkbox';
        fpsToggle.checked = false; // Default to false
        fpsToggle.style.transform = 'scale(1.2)';
        fpsToggle.id = 'fps-counter-toggle';
        
        fpsToggleContainer.appendChild(fpsToggleLabel);
        fpsToggleContainer.appendChild(fpsToggle);
        displaySection.appendChild(fpsToggleContainer);
        
        // Debug Visuals Toggle
        const debugToggleContainer = document.createElement('div');
        debugToggleContainer.style.display = 'flex';
        debugToggleContainer.style.alignItems = 'center';
        
        const debugToggleLabel = document.createElement('label');
        debugToggleLabel.textContent = 'Show Debug Visuals';
        debugToggleLabel.style.marginRight = '10px';
        debugToggleLabel.style.flexGrow = '1';
        
        const debugToggle = document.createElement('input');
        debugToggle.type = 'checkbox';
        debugToggle.checked = false; // Default to false
        debugToggle.style.transform = 'scale(1.2)';
        debugToggle.id = 'debug-visuals-toggle';
        
        debugToggleContainer.appendChild(debugToggleLabel);
        debugToggleContainer.appendChild(debugToggle);
        displaySection.appendChild(debugToggleContainer);
        
        // Back button
        const backButton = document.createElement('button');
        backButton.textContent = 'Back to Menu';
        backButton.style.display = 'block';
        backButton.style.width = '100%';
        backButton.style.padding = '10px';
        backButton.style.marginTop = '20px';
        backButton.style.backgroundColor = 'rgba(69, 60, 51, 0.9)';
        backButton.style.color = '#f5f5dc';
        backButton.style.border = '1px solid rgba(255, 215, 0, 0.5)';
        backButton.style.borderRadius = '5px';
        backButton.style.cursor = 'pointer';
        
        backButton.addEventListener('click', () => {
            this.toggleSettingsPanel();
        });
        
        // Add event listeners for settings changes
        cameraToggle.addEventListener('change', (e) => {
            if (this.onSettingChanged) {
                this.onSettingChanged('followPlayerDirection', e.target.checked);
            }
        });
        
        fpsToggle.addEventListener('change', (e) => {
            if (this.onSettingChanged) {
                this.onSettingChanged('showFpsCounter', e.target.checked);
            }
        });
        
        debugToggle.addEventListener('change', (e) => {
            if (this.onSettingChanged) {
                this.onSettingChanged('showDebugVisuals', e.target.checked);
            }
        });
        
        // Add sections to panel
        panel.appendChild(cameraSection);
        panel.appendChild(displaySection);
        panel.appendChild(backButton);
        
        return panel;
    }

    toggleVisibility(visible = null) {
        if (visible !== null) {
            this.visible = visible;
        } else {
            this.visible = !this.visible;
        }
        
        // Update display of container and blur overlay
        if (this.container) {
            this.container.style.display = this.visible ? 'block' : 'none';
            
            // Show/hide resume button based on game state
            if (this.resumeButton) {
                this.resumeButton.style.display = this.gameStarted ? 'block' : 'none';
            }
            
            // Hide controls panel when toggling main menu
            if (this.controlsPanel) {
                this.controlsPanel.style.display = 'none';
            }
            
            // Hide settings panel when toggling main menu
            if (this.settingsPanel) {
                this.settingsPanel.style.display = 'none';
            }
        }
        
        if (this.blurOverlay) {
            this.blurOverlay.style.display = this.visible ? 'block' : 'none';
        }
        
        return this.visible;
    }

    toggleControlsPanel() {
        const mainButtons = Array.from(this.container.querySelectorAll('button')).filter(
            button => button.parentElement === this.container
        );
        
        const gameName = this.container.querySelector('div:first-child');
        
        if (this.controlsPanel.style.display === 'none') {
            // Show controls panel, hide buttons
            this.controlsPanel.style.display = 'block';
            mainButtons.forEach(button => button.style.display = 'none');
            gameName.style.display = 'none';
            
            // Make sure settings panel is hidden
            if (this.settingsPanel) {
                this.settingsPanel.style.display = 'none';
            }
        } else {
            // Hide controls panel, show correct buttons
            this.controlsPanel.style.display = 'none';
            mainButtons.forEach(button => {
                // For resume button, only show if game has started
                if (button === this.resumeButton) {
                    button.style.display = this.gameStarted ? 'block' : 'none';
                } else {
                    button.style.display = 'block';
                }
            });
            gameName.style.display = 'flex';
        }
    }

    toggleSettingsPanel() {
        const mainButtons = Array.from(this.container.querySelectorAll('button')).filter(
            button => button.parentElement === this.container
        );
        
        const gameName = this.container.querySelector('div:first-child');
        
        if (this.settingsPanel.style.display === 'none') {
            // Show settings panel, hide buttons
            this.settingsPanel.style.display = 'block';
            mainButtons.forEach(button => button.style.display = 'none');
            gameName.style.display = 'none';
            
            // Make sure controls panel is hidden
            if (this.controlsPanel) {
                this.controlsPanel.style.display = 'none';
            }
        } else {
            // Hide settings panel, show correct buttons
            this.settingsPanel.style.display = 'none';
            mainButtons.forEach(button => {
                // For resume button, only show if game has started
                if (button === this.resumeButton) {
                    button.style.display = this.gameStarted ? 'block' : 'none';
                } else {
                    button.style.display = 'block';
                }
            });
            gameName.style.display = 'flex';
        }
    }

    /**
     * Updates the menu based on the current game state
     * @param {string} gameState - The current game state ('menu', 'playing', or 'paused')
     */
    updateGameState(gameState) {
        this.gameState = gameState;
        
        // Update button text based on game state
        if (this.startButton) {
            if (gameState === 'paused') {
                this.startButton.textContent = 'Restart Game';
            } else {
                this.startButton.textContent = 'Start Game';
            }
        }
        
        // Update resume button visibility
        if (this.resumeButton) {
            if (gameState === 'paused') {
                this.resumeButton.style.display = 'block';
            } else if (gameState === 'menu') {
                this.resumeButton.style.display = 'none';
            }
        }
    }

    getMenuCamera() {
        return this.menuCamera;
    }
}